import { useState, useEffect } from 'react'

const EMAIL_FUNCTIONS = [
  {
    name: 'Send-Email',
    description: 'Send plain email messages via SendGrid',
    path: '/supabase/functions/Send-Email/index.ts',
    method: 'POST',
    endpoint: '/functions/v1/Send-Email',
    features: [
      'API key authentication',
      'Custom tracking properties',
      'HTML and plain text content',
      'SendGrid integration',
      'Automatic EmailEntries logging'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from request headers (X-Api-Key or Authorization Bearer token)' },
      { step: 2, title: 'Input Validation', description: 'Ensures toEmail and message fields are present and non-empty' },
      { step: 3, title: 'Account Lookup', description: 'Retrieves EmailAccount associated with the API key, including default from email/name' },
      { step: 4, title: 'Provider Configuration', description: 'Fetches SendGrid API key and callback URL from EmailProviderConfig' },
      { step: 5, title: 'Database Logging', description: 'Creates EmailEntries record to track this email send attempt' },
      { step: 6, title: 'Tracking Properties', description: 'Stores custom tracking key-value pairs in EmailEntryAttributes table' },
      { step: 7, title: 'SendGrid Payload', description: 'Builds SendGrid API request with HTML/plain text content, custom_args for webhook tracking' },
      { step: 8, title: 'Send via SendGrid', description: 'POSTs to SendGrid API with authorization, captures x-message-id from response' },
      { step: 9, title: 'Update External ID', description: 'Stores SendGrid message ID (x-message-id) in EmailEntries.ExternalId for webhook correlation' },
      { step: 10, title: 'Return Response', description: 'Returns 202 Accepted with emailEntryId and externalId for client tracking' }
    ]
  },
  {
    name: 'Send-Email-Templated',
    description: 'Send emails using stored templates with variable interpolation',
    path: '/supabase/functions/Send-Email-Templated/index.ts',
    method: 'POST',
    endpoint: '/functions/v1/Send-Email-Templated',
    features: [
      'Template-based emails',
      'Variable interpolation ({{ }})',
      'Draft/production template support',
      'Subject line customization',
      'Template parameter validation'
    ],
    businessLogic: [
      { step: 1, title: 'Authentication', description: 'Validates API key from request headers' },
      { step: 2, title: 'Input Validation', description: 'Ensures toEmail, templateIdentifier (UUID), and templateParameters (object) are provided' },
      { step: 3, title: 'Account Lookup', description: 'Retrieves EmailAccount and associated provider configuration for the API key' },
      { step: 4, title: 'Template Retrieval', description: 'Fetches EmailTemplate by templateIdentifier for the account (checks HtmlContent or DraftHtmlContent)' },
      { step: 5, title: 'Variable Interpolation', description: 'Replaces {{ variableName }} placeholders in HTML content and subject line with templateParameters values' },
      { step: 6, title: 'Database Logging', description: 'Creates EmailEntries record with TemplateIdentifier for audit trail' },
      { step: 7, title: 'Tracking Properties', description: 'Stores custom tracking attributes in EmailEntryAttributes for analytics' },
      { step: 8, title: 'SendGrid Payload', description: 'Builds SendGrid request with interpolated content (HTML + plain text stripped version)' },
      { step: 9, title: 'Send via SendGrid', description: 'POSTs to SendGrid API, captures x-message-id for tracking' },
      { step: 10, title: 'Update & Response', description: 'Updates EmailEntries.ExternalId and returns 202 with emailEntryId and templateIdentifier' }
    ]
  },
  {
    name: 'Email-Webhook',
    description: 'Handle SendGrid delivery event webhooks',
    path: '/supabase/functions/Email-Webhook/index.ts',
    method: 'POST',
    endpoint: '/functions/v1/Email-Webhook',
    features: [
      'Ed25519 signature verification',
      'Delivery event tracking',
      'Automatic event type creation',
      'Callback URL forwarding',
      'Batch event processing'
    ],
    businessLogic: [
      { step: 1, title: 'Signature Verification', description: 'Validates Ed25519 signature using X-Twilio-Email-Event-Webhook headers (if public key configured)' },
      { step: 2, title: 'Parse Events', description: 'Parses JSON body into array of events (SendGrid sends batches)' },
      { step: 3, title: 'Event Type Management', description: 'For each event, gets or creates EventType record (e.g., "delivered", "bounced", "opened")' },
      { step: 4, title: 'Resolve EmailEntryId', description: 'Extracts EmailEntryId from custom_args or looks up by sg_message_id/smtp-id in EmailEntries table' },
      { step: 5, title: 'Extract Event Data', description: 'Parses event fields: status, reason, smtp-id, sg_message_id, timestamp' },
      { step: 6, title: 'Store Delivery Event', description: 'Inserts into EmailEntryDeliveryEvents with event type, external ID, status, reason, and timestamp' },
      { step: 7, title: 'Callback Forwarding', description: 'If CallbackUrl exists in custom_args, forwards the event to client\'s webhook endpoint' },
      { step: 8, title: 'Retry Logic', description: 'If EmailEntryId not found immediately (race condition), retries after 250ms delay' },
      { step: 9, title: 'Error Handling', description: 'Logs errors per event but continues processing remaining events in batch' },
      { step: 10, title: 'Return Summary', description: 'Returns 202 with count of events received and per-event success/failure results' }
    ]
  }
]

export default function EdgeFunctionsTab() {
  const [selectedFunction, setSelectedFunction] = useState(null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex items-center space-x-3 mb-2">
          <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Edge Functions
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          View and explore email-related Supabase Edge Functions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Function List */}
        <div className="lg:col-span-1 space-y-3">
          {EMAIL_FUNCTIONS.map((func) => (
            <button
              key={func.name}
              onClick={() => setSelectedFunction(func)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                selectedFunction?.name === func.name
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-purple-300 dark:hover:border-purple-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                      {func.name}
                    </h3>
                    <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                      {func.method}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {func.description}
                  </p>
                  <code className="text-xs text-purple-600 dark:text-purple-400 mt-2 block">
                    {func.endpoint}
                  </code>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Function Details */}
        <div className="lg:col-span-2">
          {selectedFunction ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
              {/* Function Header */}
              <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 sm:p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold">{selectedFunction.name}</h3>
                    <p className="text-purple-100 mt-1 text-sm sm:text-base">{selectedFunction.description}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFunction(null)}
                    className="p-1 hover:bg-white/20 rounded-lg transition"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                    {selectedFunction.method}
                  </span>
                  <code className="px-3 py-1 bg-black/20 rounded-full text-sm font-mono">
                    {selectedFunction.endpoint}
                  </code>
                </div>
              </div>

              {/* Features */}
              <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Key Features
                </h4>
                <ul className="space-y-2">
                  {selectedFunction.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business Logic Flow */}
              <div className="p-4 sm:p-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Business Logic Flow
                </h4>
                <div className="space-y-3">
                  {selectedFunction.businessLogic.map((logic, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                          {logic.step}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {logic.title}
                        </h5>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {logic.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* API Example */}
              <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  API Example
                </h4>
                <pre className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto text-xs">
                  <code className="text-gray-800 dark:text-gray-200">{`curl -X ${selectedFunction.method} \\
  'https://your-project.supabase.co${selectedFunction.endpoint}' \\
  -H 'X-Api-Key: your-api-key' \\
  -H 'Content-Type: application/json' \\
  -d '{
  // Request payload here
}'`}</code>
                </pre>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Select a Function
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Choose an Edge Function from the list to view its implementation and documentation
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
